import { SkeletonBodyText, SkeletonDisplayText } from "@shopify/polaris";

import { useAdminLoading } from "../utils/adminLoading";

/**
 * The loading picture for /analytics (components/AnalyticsDashboard.jsx).
 *
 * That route previously fell back to RouteLoader — a large centred spinner in
 * `minHeight: 60vh` — while its chunk downloaded. /analytics is the heaviest
 * chunk in the app (Recharts, five tab renderers, a dozen card components), so
 * it is the route where the generic spinner was on screen longest and the jump
 * to the real page was largest. See PageSkeleton.jsx for the write-up of the
 * problem this whole family of components solves.
 *
 * ⚠️ NOT built on <PageSkeleton>. That primitive draws N stacked full-width
 * sections; /analytics above the fold is a controls bar, a tab strip, a 4-up
 * stat-tile grid and a 2fr/1fr chart row. Stacked sections would land nothing
 * where the real content lands, which is the layout jump this is meant to
 * remove. So the structure below is hand-rolled and mirrors
 * AnalyticsDashboard's own main render:
 *
 *   AnalyticsDashboard.jsx:1083  <s-page heading="Analytics" inlineSize="large">
 *   AnalyticsDashboard.jsx:1102  <s-box paddingBlockEnd="base"> controls
 *   AnalyticsDashboard.jsx:1105  DateRangePills (a date button + a compare chip)
 *   AnalyticsDashboard.jsx:1111  "Data:" + Auto / Live / Demo buttons
 *   AnalyticsDashboard.jsx:1143  <Tabs> — 5 tabs (:184-190)
 *   AnalyticsDashboard.jsx:1145  one line of tab description
 *   AnalyticsDashboard.jsx:689   ShopifyMetricGrid — 4 tiles, minmax(180px, 1fr)
 *   AnalyticsDashboard.jsx:692   AttributionMetricsCard — one full-width section
 *   AnalyticsDashboard.jsx:700   4 insight cards, minmax(220px, 1fr)
 *   AnalyticsDashboard.jsx:728   chart (2fr) + breakdown (1fr), 350px tall
 *
 * Everything below that chart row (locations/devices, benchmarks/insights) is
 * off-screen on first paint at any realistic viewport, so it is deliberately
 * not drawn — reserving space for content nobody can see buys nothing.
 *
 * ⚠️ `heading` must stay byte-identical to AnalyticsDashboard.jsx:1083's
 * `<s-page heading="Analytics" inlineSize="large">`, including inlineSize —
 * "large" widens the page, so getting it wrong shifts every column at handoff.
 *
 * NOTE ON PRIMITIVES: SkeletonDisplayText / SkeletonBodyText are Polaris REACT
 * holdouts (there is no s-skeleton-* web component) — the same holdout
 * PageSkeleton.jsx and Analytics/ShopifyMetricGrid.jsx already rely on. The
 * tile shell below is copied from ShopifyMetricGrid's own
 * ShopifyMetricCardSkeleton (:64-78) so the loading tile and the real tile are
 * the same box.
 */

/** A grey pill standing in for one button/chip in the controls bar or tab strip. */
function Pill({ inlineSize }) {
  return (
    <s-box
      background="subdued"
      borderRadius="base"
      inlineSize={inlineSize}
      blockSize="28px"
    />
  );
}

/** One stat tile — same shell as Analytics/ShopifyMetricGrid.jsx:64-78. */
function TileSkeleton() {
  return (
    <s-box background="base" padding="base" borderRadius="base" border="base">
      <s-box paddingBlockEnd="small-200">
        <SkeletonBodyText lines={1} />
      </s-box>
      <SkeletonDisplayText size="medium" />
    </s-box>
  );
}

export default function AnalyticsSkeleton() {
  // Not rendering <PageSkeleton>, so this component owns the admin header
  // progress bar for its own lifetime. See utils/adminLoading.js.
  useAdminLoading();

  return (
    <s-page heading="Analytics" inlineSize="large">
      {/* Controls bar + tab strip — AnalyticsDashboard.jsx:1102-1149 */}
      <s-box paddingBlockEnd="base">
        <s-stack direction="block" gap="small">
          <s-stack direction="inline" gap="base" alignItems="center">
            {/* DateRangePills: the range button, then the "Compare to:" chip */}
            <Pill inlineSize="140px" />
            <Pill inlineSize="180px" />
            {/* "Data:" label + Auto / Live / Demo */}
            <s-stack direction="inline" gap="small-300" alignItems="center">
              <Pill inlineSize="48px" />
              <Pill inlineSize="64px" />
              <Pill inlineSize="64px" />
              <Pill inlineSize="64px" />
            </s-stack>
          </s-stack>

          {/* Tabs — 5 of them (AnalyticsDashboard.jsx:184-190) */}
          <s-stack direction="inline" gap="small-200" alignItems="center">
            <Pill inlineSize="88px" />
            <Pill inlineSize="104px" />
            <Pill inlineSize="152px" />
            <Pill inlineSize="80px" />
            <Pill inlineSize="96px" />
          </s-stack>

          {/* One-line tab description */}
          <SkeletonBodyText lines={1} />
        </s-stack>
      </s-box>

      <s-stack direction="block" gap="large">
        {/* ShopifyMetricGrid — 4 tiles */}
        <s-box paddingBlockEnd="base">
          <s-grid
            gridTemplateColumns="repeat(auto-fit, minmax(180px, 1fr))"
            gap="base"
          >
            <TileSkeleton />
            <TileSkeleton />
            <TileSkeleton />
            <TileSkeleton />
          </s-grid>
        </s-box>

        {/* AttributionMetricsCard — one full-width section */}
        <s-section>
          <s-stack direction="block" gap="base">
            <SkeletonDisplayText size="small" />
            <SkeletonBodyText lines={3} />
          </s-stack>
        </s-section>

        {/* Batched insight cards — coupon funnel / forms / suppression / scroll */}
        <s-grid
          gridTemplateColumns="repeat(auto-fit, minmax(220px, 1fr))"
          gap="base"
        >
          <s-section>
            <s-stack direction="block" gap="small">
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={2} />
            </s-stack>
          </s-section>
          <s-section>
            <s-stack direction="block" gap="small">
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={2} />
            </s-stack>
          </s-section>
          <s-section>
            <s-stack direction="block" gap="small">
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={2} />
            </s-stack>
          </s-section>
          <s-section>
            <s-stack direction="block" gap="small">
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={2} />
            </s-stack>
          </s-section>
        </s-grid>

        {/* Impressions chart (2fr) + performance breakdown (1fr).
            Same container query as AnalyticsDashboard.jsx:729 so the columns
            collapse at exactly the same width the real row does. */}
        <s-query-container>
          <s-grid
            gridTemplateColumns="@container (inline-size <= 768px) 1fr, 2fr 1fr"
            gap="base"
          >
            <s-section>
              <s-stack direction="block" gap="base">
                <SkeletonDisplayText size="small" />
                {/* The chart is rendered at height={350} — reserve it. */}
                <s-box
                  background="subdued"
                  borderRadius="base"
                  blockSize="350px"
                />
              </s-stack>
            </s-section>
            <s-section>
              <s-stack direction="block" gap="base">
                <SkeletonDisplayText size="small" />
                <SkeletonBodyText lines={6} />
              </s-stack>
            </s-section>
          </s-grid>
        </s-query-container>
      </s-stack>
    </s-page>
  );
}
